"use client";

/**
 * Loom-style custom video player shell. Media state lives in
 * `useClipMediaEngine`; chrome is derived via `deriveClipMediaUi`.
 */

import { cn } from "@v1/ui/utils";
import { Play } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactElement, KeyboardEvent as ReactKeyboardEvent } from "react";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { ClipVideoControls } from "./clip-video-controls";
import type { ClipVideoPlayerProps } from "./clip-video-player-types";
import {
  CONTROLS_HIDE_DELAY_MS,
  SKIP_SECONDS,
} from "./clip-video-player-types";
import { deriveClipMediaUi } from "./derive-clip-media-ui";
import { useClipHls } from "./use-clip-hls";
import { useClipMediaEngine } from "./use-clip-media-engine";

export type { ClipVideoPlayerProps } from "./clip-video-player-types";

/** iOS Safari exposes video-only fullscreen via WebKit, not Element.requestFullscreen. */
interface WebKitVideoElement extends HTMLVideoElement {
  readonly webkitDisplayingFullscreen?: boolean;
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
}

function asWebKitVideo(
  video: HTMLVideoElement | null,
): WebKitVideoElement | null {
  return video;
}

export const ClipVideoPlayer = forwardRef<
  HTMLVideoElement,
  ClipVideoPlayerProps
>(function ClipVideoPlayer(
  {
    src,
    poster,
    autoPlay = false,
    seekingDisabled = false,
    className,
    onError,
    onLoadedData,
  },
  forwardedRef,
): ReactElement {
  const t = useTranslations("clips.clipVideoPlayer");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<number | null>(null);

  const setVideoNode = useCallback(
    (node: HTMLVideoElement | null): void => {
      videoRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const {
    snapshot,
    videoHandlers,
    togglePlay,
    seekTo,
    skip,
    toggleMute,
    changeVolume,
    setPlaybackRate,
  } = useClipMediaEngine(videoRef, { src, onError, onLoadedData });

  // `videoSrc` comes back undefined while hls.js feeds the element through MSE:
  // a src on the element would fetch the playlist a second time, natively.
  const { quality, videoSrc } = useClipHls(videoRef, { src, onError });

  const [scrubbing, setScrubbing] = useState(false);
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [pipSupported, setPipSupported] = useState(false);
  // iPhone Safari has no Element.requestFullscreen — fullscreen there means the
  // NATIVE video player, whose chrome has its own seek bar. With the seeking
  // lock that surface must stay closed, so the button vanishes entirely.
  const [elementFullscreenSupported, setElementFullscreenSupported] =
    useState(true);
  const menuOpen = speedOpen || qualityOpen;

  const ui = deriveClipMediaUi(snapshot, scrubbing, scrubTime);
  const playing = !snapshot.paused;

  // Drop scrub intent once the element caught up (avoids bar snap-back).
  useEffect(() => {
    if (scrubbing || scrubTime === null) return;
    const closeEnough = Math.abs(snapshot.currentTime - scrubTime) < 0.35;
    if (snapshot.seeking && !closeEnough) return;
    setScrubTime(null);
  }, [scrubbing, scrubTime, snapshot.seeking, snapshot.currentTime]);

  useEffect(() => {
    setScrubbing(false);
    setScrubTime(null);
  }, [src]);

  useEffect(() => {
    setPipSupported(document.pictureInPictureEnabled);
    setElementFullscreenSupported(
      typeof document.documentElement.requestFullscreen === "function",
    );
  }, []);

  // Native seek surfaces the in-player chrome cannot reach: Chrome's Global
  // Media Controls and OS media widgets scrub any playing video unless the
  // page claims the seek actions. No-op handlers close that path while the
  // lock is active.
  useEffect(() => {
    if (!seekingDisabled) return;
    if (!("mediaSession" in navigator)) return;
    const actions: readonly MediaSessionAction[] = [
      "seekto",
      "seekbackward",
      "seekforward",
    ];
    for (const action of actions) {
      try {
        navigator.mediaSession.setActionHandler(action, () => {});
      } catch {
        /* action not supported by this browser */
      }
    }
    return () => {
      for (const action of actions) {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          /* action not supported by this browser */
        }
      }
    };
  }, [seekingDisabled]);

  const scheduleHide = useCallback((): void => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = window.setTimeout(() => {
      setControlsVisible(false);
    }, CONTROLS_HIDE_DELAY_MS);
  }, []);

  const revealControls = useCallback((): void => {
    setControlsVisible(true);
    const video = videoRef.current;
    if (video && !video.paused) scheduleHide();
  }, [scheduleHide]);

  useEffect(() => {
    if (!playing || scrubbing || menuOpen) {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setControlsVisible(true);
    } else {
      scheduleHide();
    }
    return () => {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
      }
    };
  }, [playing, scrubbing, menuOpen, scheduleHide]);

  useEffect(() => {
    const onFsChange = (): void => {
      setFullscreen(document.fullscreenElement === containerRef.current);
    };
    const onWebkitBegin = (): void => {
      setFullscreen(true);
    };
    const onWebkitEnd = (): void => {
      setFullscreen(false);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    const video = asWebKitVideo(videoRef.current);
    video?.addEventListener("webkitbeginfullscreen", onWebkitBegin);
    video?.addEventListener("webkitendfullscreen", onWebkitEnd);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      video?.removeEventListener("webkitbeginfullscreen", onWebkitBegin);
      video?.removeEventListener("webkitendfullscreen", onWebkitEnd);
    };
  }, [src]);

  const toggleFullscreen = useCallback((): void => {
    const container = containerRef.current;
    const video = asWebKitVideo(videoRef.current);
    if (!container) return;

    if (document.fullscreenElement === container) {
      void document.exitFullscreen().catch(() => {});
      return;
    }
    if (video?.webkitDisplayingFullscreen) {
      video.webkitExitFullscreen?.();
      return;
    }
    if (typeof container.requestFullscreen === "function") {
      void container.requestFullscreen().catch(() => {});
      return;
    }
    // iOS video-only fullscreen shows the NATIVE player incl. its seek bar —
    // with the seeking lock that would reopen everything the lock closes.
    if (seekingDisabled) return;
    if (typeof video?.webkitEnterFullscreen === "function") {
      video.webkitEnterFullscreen();
    }
  }, [seekingDisabled]);

  const togglePip = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;
    if (document.pictureInPictureElement === video) {
      void document.exitPictureInPicture().catch(() => {});
    } else if (document.pictureInPictureEnabled) {
      void video.requestPictureInPicture().catch(() => {});
    }
  }, []);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>): void => {
      switch (event.key) {
        case " ":
        case "k":
          event.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          event.preventDefault();
          if (!seekingDisabled) skip(-SKIP_SECONDS);
          break;
        case "ArrowRight":
          event.preventDefault();
          if (!seekingDisabled) skip(SKIP_SECONDS);
          break;
        case "ArrowUp":
          event.preventDefault();
          changeVolume((videoRef.current?.volume ?? 1) + 0.1);
          break;
        case "ArrowDown":
          event.preventDefault();
          changeVolume((videoRef.current?.volume ?? 1) - 0.1);
          break;
        case "m":
          toggleMute();
          break;
        case "f":
          toggleFullscreen();
          break;
        default:
          break;
      }
      revealControls();
    },
    [
      togglePlay,
      skip,
      seekingDisabled,
      changeVolume,
      toggleMute,
      toggleFullscreen,
      revealControls,
    ],
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        "group/player relative aspect-video w-full select-none overflow-hidden rounded-2xl bg-black shadow-lg ring-1 ring-black/[0.06]",
        !controlsVisible && playing && "cursor-none",
        className,
      )}
      onPointerMove={revealControls}
      onKeyDown={onKeyDown}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: player shell takes focus for Space/arrow shortcuts; controls stay individually focusable.
      tabIndex={0}
    >
      <video
        ref={setVideoNode}
        src={videoSrc}
        poster={poster ?? undefined}
        autoPlay={autoPlay}
        playsInline
        preload="auto"
        className="absolute inset-0 h-full w-full bg-black"
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
        {...videoHandlers}
      />

      {ui.showSpinner ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-white/30 border-t-white" />
        </div>
      ) : null}

      {ui.showCenterPlay ? (
        <button
          type="button"
          onClick={togglePlay}
          aria-label={t("abspielen")}
          className="absolute inset-0 z-10 flex items-center justify-center"
        >
          <span className="flex h-[74px] w-[74px] items-center justify-center rounded-full bg-white/95 shadow-xl transition-transform duration-150 hover:scale-105">
            <Play
              className="ml-1 h-8 w-8 text-neutral-900"
              fill="currentColor"
              strokeWidth={0}
            />
          </span>
        </button>
      ) : null}

      <ClipVideoControls
        snapshot={snapshot}
        quality={quality}
        displayTime={ui.displayTime}
        progressRatio={ui.progressRatio}
        bufferedRatio={ui.bufferedRatio}
        effectiveVolume={ui.effectiveVolume}
        controlsVisible={controlsVisible}
        fullscreen={fullscreen}
        // Safari's PiP window carries its own scrub bar — locked clips lose PiP.
        pipSupported={pipSupported && !seekingDisabled}
        showFullscreen={!seekingDisabled || elementFullscreenSupported}
        seekingDisabled={seekingDisabled}
        onTogglePlay={togglePlay}
        onSkip={skip}
        onToggleMute={toggleMute}
        onChangeVolume={changeVolume}
        onSetPlaybackRate={setPlaybackRate}
        onTogglePip={togglePip}
        onToggleFullscreen={toggleFullscreen}
        onScrubbingChange={setScrubbing}
        onScrubTimeChange={setScrubTime}
        onSeek={seekTo}
        onSpeedMenuChange={setSpeedOpen}
        onQualityMenuChange={setQualityOpen}
      />
    </div>
  );
});
